import { zodResolver } from '@hookform/resolvers/zod'
import { raise } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { IntegratorType } from '../components/ref-badge/RefBadge'
import {
  DepositToSavingsTxOutcome,
  ExtendedDepositToSavingsTxOutcome,
  SavingsDepositTxOverview,
  StusdsWarningData,
} from '../types'
import { createTxOverview } from './createTxOverview'
import { useStusdsWarningData } from './stusds/useStusdsWarningData'
import { useActiveIntegrator } from './useActiveIntegrator'
import { useDepositToSavingsValidator } from './useDepositToSavingsValidator'
import { useFormFieldsForSavingsDepositDialog } from './useFormFieldForSavingsDepositDialog'
import { getChainConfigEntry } from '@/config/chain'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useSimulationTimestamp } from '@/domain/hooks/useSimulationTimestamp'
import { useWalletType } from '@/domain/hooks/useWalletType'
import { useSandbox } from '@/domain/sandbox'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { useDebouncedFormValues } from '@/features/dialogs/common/logic/transfer-from-user/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'
import { getAccountDisplayedUnderlyingToken } from '@/features/savings/utils/getAccountDisplayedUnderlyingToken'
import { useOngoingCampaignsQuery } from '@/features/spark-rewards/data/useOngoingCampaignsQuery'

export interface UseSavingsDepositDialogParams {
  initialToken: Token
  savingsToken: Token
  chainId?: number
  onSuccess?: () => void
}

export interface UseSavingsDepositDialogResults {
  savingsToken: Token
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  objectives: Objective[]
  tokenToDeposit: TokenWithValue
  pageStatus: PageStatus
  txOverview: SavingsDepositTxOverview
  underlyingToken: Token
  accountName: string
  form: UseFormReturn<AssetInputSchema>
  depositTxOutcome?: ExtendedDepositToSavingsTxOutcome
  stusdsWarningData?: StusdsWarningData
  activeIntegrator: IntegratorType | undefined
  actionsViewState: ActionsViewState
}

export function useSavingsDepositDialog({
  savingsToken,
  initialToken,
  chainId: _chainId,
  onSuccess,
}: UseSavingsDepositDialogParams): UseSavingsDepositDialogResults {
  const currentChainId = useChainId()
  const chainId = _chainId ?? currentChainId

  const { walletType } = useWalletType()
  const { tenderlyClient, isInSandbox } = useSandbox()
  const { simulationTimestamp, refresh, isFetching } = useSimulationTimestamp({ chainId })

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    void (async () => {
      if (tenderlyClient && isInSandbox) {
        await tenderlyClient.mineBlocks(1n)
      }
      refresh()
    })()
  }, [chainId, tenderlyClient, isInSandbox])

  const chainConfig = getChainConfigEntry(chainId)
  const savingsConfig = chainConfig.savings ?? raise('Savings config not available for this chain')
  const ongoingCampaignsResult = useOngoingCampaignsQuery()
  const stusdsWarningData = useStusdsWarningData()
  const activeIntegrator = useActiveIntegrator({ savingsToken })

  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const tokenRepository = useSavingsTokenRepository({ chainId, account })

  const selectedAccountConfig =
    savingsConfig.accounts.find((acc) => acc.savingsToken === savingsToken.symbol) ??
    raise('Savings account is not found')
  const supportedAssetsWithBalance = selectedAccountConfig.supportedAssets.map((symbol) => ({
    token: tokenRepository.findOneBySymbol(symbol),
    balance: tokenRepository.findOneBalance(symbol),
  }))
  const savingsAccounts = useSavingsAccountRepository({ chainId, timestamp: simulationTimestamp })

  const savingsAccount = savingsAccounts.findOneBySavingsToken(savingsToken)

  const displayedUnderlyingToken = getAccountDisplayedUnderlyingToken({
    account: savingsAccount,
    chainId,
  })

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const accountName =
    savingsAccount.savingsToken.symbol === TokenSymbol('stUSDS')
      ? 'Sky stUSDS'
      : `${displayedUnderlyingToken.symbol} Savings`

  const validator = useDepositToSavingsValidator({
    chainId,
    tokenRepository,
    savingsAccount,
  })
  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(validator),
    defaultValues: {
      symbol: initialToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const assetsFields = useFormFieldsForSavingsDepositDialog({
    form,
    savingsAccount,
    tokenRepository,
    chainId,
  })

  const txOverview = createTxOverview({
    tokenRepository,
    formValues,
    savingsAccount,
    ongoingCampaigns: ongoingCampaignsResult.data,
    accountName,
    chainId,
  })

  const [depositTxOutcome, setDepositTxOutcome] = useState<DepositToSavingsTxOutcome | undefined>(undefined)

  const objectives: Objective[] = [
    {
      type: 'depositToSavings',
      value: formValues.value,
      token: formValues.token,
      savingsToken,
    },
  ]

  const tokenToDeposit: TokenWithValue = {
    token: formValues.token,
    value: formValues.value,
  }
  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing && !isFetching

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      savingsAccounts,
      walletType,
      chainId,
      saveTxOutcome: (txOutcome) => {
        if (txOutcome.type === 'depositToSavings') {
          setDepositTxOutcome(txOutcome)
        }
      },
    },
    onFinish: () => {
      if (onSuccess) {
        onSuccess()
      } else {
        setPageStatus('success')
      }
    },
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  const extendedDepositTxOutcome: ExtendedDepositToSavingsTxOutcome | undefined = depositTxOutcome
    ? {
        ...depositTxOutcome,
        savingsToken,
        underlyingToken: savingsAccount.underlyingToken,
        underlyingTokenReceived: savingsAccount.converter.convertToAssets({
          shares: depositTxOutcome.savingsTokenAmountReceived,
        }),
        inputToken: formValues.token,
      }
    : undefined

  return {
    savingsToken,
    selectableAssets: supportedAssetsWithBalance,
    assetsFields,
    underlyingToken: savingsAccount.underlyingToken,
    form,
    objectives,
    tokenToDeposit,
    txOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => {
        if (onSuccess) {
          onSuccess()
        } else {
          setPageStatus('success')
        }
      },
    },
    depositTxOutcome: extendedDepositTxOutcome,
    accountName,
    stusdsWarningData,
    activeIntegrator,
    actionsViewState,
  }
}
